import { parseComplexCSV } from '@utils/csvParser.js';
import { downloadFile } from '@utils/fileHandler.js';
import { generateShopifyCsv } from '@utils/shopifyCsvGenerator.js';
import { parseShoplineV1Xlsx } from '@utils/xlsxParser.js';

/**
 * ShopifyConverter Alpine component
 * Handles SHOPLINE to Shopify CSV conversion
 */
export function shopifyConverter() {
  return {
    // File upload state
    uploadedFile: null,
    fileName: null,
    fileSize: null,
    isDragging: false,
    fileType: null,           // 'csv' | 'xlsx'
    detectedPlatform: null,   // 'shopline' | 'shopline-v1' | 'shopify'

    // Parsed data state
    parsedData: [],
    headers: [],
    csvFormat: null,

    // Field mapping state
    fieldMappings: [],
    showAllMappings: false,

    // Statistics state
    stats: {
      imageCount: 0,
      mappedFieldCount: 0,
      productCount: 0,
      variantCount: 0
    },

    // Processing state
    processing: false,
    processingStage: '',
    processingProgress: 0,

    // Export state
    shopifyCsv: '',
    exportReady: false,
    exportFileName: '',

    // Sidebar steps state
    steps: [
      { id: 'upload-source', label: 'Upload Source', status: 'pending', subtitle: 'Waiting for file' },
      { id: 'map-fields', label: 'Map Fields', status: 'pending', subtitle: '' },
      { id: 'preview', label: 'Preview Data', status: 'pending', subtitle: '' },
      { id: 'export', label: 'Export Shopify CSV', status: 'pending', subtitle: '' }
    ],

    /**
     * Initialize global drag/drop listeners for the drop zone
     */
    initDropZone() {
      const hasFiles = (event) =>
        event?.dataTransfer && [...event.dataTransfer.types || []].includes('Files');

      const handleWindowDragOver = (event) => {
        if (!hasFiles(event)) {return;}
        event.preventDefault();
        this.isDragging = true;
      };

      const handleWindowDragLeave = (event) => {
        if (!hasFiles(event)) {return;}
        if (event.relatedTarget === null) {
          this.isDragging = false;
        }
      };

      const handleWindowDrop = (event) => {
        if (!hasFiles(event)) {return;}
        event.preventDefault();
        this.isDragging = false;
      };

      window.addEventListener('dragover', handleWindowDragOver);
      window.addEventListener('dragleave', handleWindowDragLeave);
      window.addEventListener('drop', handleWindowDrop);

      return () => {
        window.removeEventListener('dragover', handleWindowDragOver);
        window.removeEventListener('dragleave', handleWindowDragLeave);
        window.removeEventListener('drop', handleWindowDrop);
      };
    },

    /**
     * Handle file selection from input
     */
    handleFileSelect(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },

    /**
     * Handle drag and drop
     */
    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },

    /**
     * Process uploaded file
     */
    async processFile(file) {
      if (!file) {
        alert('Please select a file');
        return;
      }

      // 验证文件扩展名
      const fileName = file.name.toLowerCase();
      const isCsv = fileName.endsWith('.csv');
      const isXlsx = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

      if (!isCsv && !isXlsx) {
        alert('Please select a valid CSV or XLSX file');
        return;
      }

      this.uploadedFile = file;
      this.fileName = file.name;
      this.fileSize = this.formatFileSize(file.size);
      this.fileType = isXlsx ? 'xlsx' : 'csv';
      this.processing = true;
      this.processingProgress = 0;
      this.processingStage = 'Reading file...';

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          let result;

          if (isXlsx) {
            // XLSX 处理分支
            this.processingStage = 'Parsing XLSX file...';
            this.processingProgress = 20;
            await new Promise(resolve => setTimeout(resolve, 50));

            const arrayBuffer = e.target.result;
            result = parseShoplineV1Xlsx(arrayBuffer);
            this.detectedPlatform = 'shopline-v1';
          } else {
            // CSV 处理分支
            this.processingStage = 'Parsing CSV file...';
            this.processingProgress = 20;
            await new Promise(resolve => setTimeout(resolve, 50));

            const csv = e.target.result;
            result = parseComplexCSV(csv);
            this.detectedPlatform = result.csvFormat;
          }

          this.parsedData = result.parsedData;
          this.headers = result.headers;
          this.csvFormat = result.csvFormat;

          // 统一的后续处理流程
          this.processingProgress = 50;
          this.processingStage = 'Generating field mappings...';
          await new Promise(resolve => setTimeout(resolve, 50));
          this.generateFieldMappings();

          this.processingProgress = 70;
          this.processingStage = 'Calculating statistics...';
          await new Promise(resolve => setTimeout(resolve, 50));
          this.calculateStats();

          this.processingProgress = 90;
          this.processingStage = 'Generating Shopify CSV...';
          await new Promise(resolve => setTimeout(resolve, 50));
          this.generateShopifyCsv();

          this.processingProgress = 100;
          this.processingStage = 'Complete!';
          await new Promise(resolve => setTimeout(resolve, 300));

          this.exportReady = true;
          this.exportFileName = `shopify_import_ready_${new Date().toISOString().split('T')[0]}.csv`;

          // 更新步骤状态
          const platformName = this.detectedPlatform === 'shopline-v1' ? 'SHOPLINE 1.0' :
                               this.detectedPlatform === 'shopline' ? 'SHOPLINE 2.0' : 'Shopify';

          this.steps[0].status = 'completed';
          this.steps[0].subtitle = `${this.fileType.toUpperCase()} uploaded (${platformName})`;
          this.steps[1].status = 'completed';
          this.steps[1].subtitle = `${this.stats.mappedFieldCount} fields mapped`;
          this.steps[2].status = 'completed';
          this.steps[2].subtitle = `${this.stats.productCount} products, ${this.stats.variantCount} variants`;
          this.steps[3].status = 'completed';
          this.steps[3].subtitle = 'Ready to download';

          this.processing = false;
          this.processingProgress = 0;
          this.processingStage = '';
        } catch (error) {
          console.error('Error processing file:', error);
          this.processing = false;
          this.processingProgress = 0;
          this.processingStage = '';
          alert(`Error processing file: ${error.message}`);
        }
      };

      reader.onerror = () => {
        this.processing = false;
        this.processingProgress = 0;
        this.processingStage = '';
        alert('Failed to read file. Please try again.');
      };

      // 根据文件类型选择读取方式
      if (isXlsx) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    },

    /**
     * Generate field mappings preview
     */
    generateFieldMappings() {
      // 根据检测到的平台选择不同的字段映射
      const isShoplineV1 = this.csvFormat === 'shopline-v1';

      const mappings = isShoplineV1 ? [
        // SHOPLINE 1.0 特定映射
        { sample: '', source: 'Product Name (English)', status: 'mapped', target: 'Title' },
        { sample: '', source: 'Product Summary (Traditional Chinese)', status: 'mapped', target: 'Body (HTML)' },
        { sample: '', source: 'Product Summary (English)', status: 'mapped', target: 'Body (HTML)' },
        { sample: '', source: 'Brand', status: 'mapped', target: 'Vendor' },
        { sample: '', source: 'Regular Price', status: 'mapped', target: 'Variant Price' },
        { sample: '', source: 'Sale Price', status: 'mapped', target: 'Variant Compare At Price' },
        { sample: '', source: 'Quantity (DO NOT EDIT)', status: 'mapped', target: 'Variant Inventory Qty' },
        { sample: '', source: 'Image', status: 'mapped', target: 'Image Src' },
        { sample: '', source: 'Images', status: 'mapped', target: 'Image Src' },
        { sample: '', source: 'handle', status: 'mapped', target: 'Handle' },
        { sample: '', source: 'SKU', status: 'mapped', target: 'Variant SKU' },
        { sample: '', source: 'Variant SKU', status: 'mapped', target: 'Variant SKU' },
        { sample: '', source: 'Product Tag', status: 'mapped', target: 'Tags' },
        { sample: '', source: 'Online Store Categories', status: 'mapped', target: 'Type' },
        { sample: '', source: 'Variant (English) (DO NOT EDIT)', status: 'mapped', target: 'Option1 Value' },
        { sample: '', source: 'Weight(KG)', status: 'mapped', target: 'Variant Weight' },
        { sample: '', source: 'SEO Title (English)', status: 'mapped', target: 'SEO Title' },
        { sample: '', source: 'SEO Description (English)', status: 'mapped', target: 'SEO Description' },
      ] : [
        // SHOPLINE 2.0 原有映射（保持不变）
        { sample: '', source: 'Title*', status: 'mapped', target: 'Title' },
        { sample: '', source: 'Product description html', status: 'mapped', target: 'Body (HTML)' },
        { sample: '', source: 'Vendor', status: 'mapped', target: 'Vendor' },
        { sample: '', source: 'SKU price', status: 'mapped', target: 'Variant Price' },
        { sample: '', source: 'SKU Inventory Quantity', status: 'mapped', target: 'Variant Inventory Qty' },
        { sample: '', source: 'Master image', status: 'mapped', target: 'Image Src' },
        { sample: '', source: 'Handle', status: 'mapped', target: 'Handle' },
        { sample: '', source: 'Tags', status: 'mapped', target: 'Tags' },
        { sample: '', source: 'Collections', status: 'mapped', target: 'Type' },
        { sample: '', source: 'Product type', status: 'mapped', target: 'Product Category' },
        { sample: '', source: 'SKU name', status: 'mapped', target: 'Variant Name' },
        { sample: '', source: 'Option1 Name', status: 'mapped', target: 'Option1 Name' },
        { sample: '', source: 'Option1 Value', status: 'mapped', target: 'Option1 Value' },
        { sample: '', source: 'Option2 Name', status: 'mapped', target: 'Option2 Name' },
        { sample: '', source: 'Option2 Value', status: 'mapped', target: 'Option2 Value' },
        { sample: '', source: 'Option3 Name', status: 'mapped', target: 'Option3 Name' },
        { sample: '', source: 'Option3 Value', status: 'mapped', target: 'Option3 Value' },
        { sample: '', source: 'SKU original price', status: 'mapped', target: 'Variant Compare At Price' },
        { sample: '', source: 'Variant weight', status: 'mapped', target: 'Variant Weight' },
        { sample: '', source: 'Variant weight unit', status: 'mapped', target: 'Variant Weight Unit' },
        { sample: '', source: 'Continue selling when out of stock', status: 'mapped', target: 'Variant Inventory Policy' },
        { sample: '', source: 'Shipping origin', status: 'mapped', target: 'Requires Shipping' },
        { sample: '', source: 'SEO title', status: 'mapped', target: 'SEO Title' },
        { sample: '', source: 'SEO description', status: 'mapped', target: 'SEO Description' },
      ];

      // 从实际数据更新样本值
      if (this.parsedData.length > 0) {
        const firstRow = this.parsedData[0];
        mappings.forEach(mapping => {
          const sampleValue = firstRow[mapping.source] || '';
          mapping.sample = this.truncateValue(sampleValue, 50);
        });
      }

      this.fieldMappings = mappings;
    },

    /**
     * Calculate conversion statistics
     */
    calculateStats() {
      const uniqueHandles = new Set();
      let totalVariants = 0;
      let totalImages = 0;

      this.parsedData.forEach(row => {
        // 兼容 SHOPLINE 2.0 和 SHOPLINE 1.0 的 Handle 字段
        const handle = row['Handle'] || row['handle'];
        if (handle) {
          uniqueHandles.add(handle);
        }
        totalVariants++;

        // 兼容不同平台的图片字段
        if (row['Master image']) { totalImages++; }
        if (row['Additional image']) { totalImages++; }
        if (row['SKU image']) { totalImages++; }
        // SHOPLINE 1.0 图片字段（可能是数组或逗号分隔字符串）
        const shoplineV1Images = row['Image'] || row['images'];
        if (shoplineV1Images) {
          const imgs = Array.isArray(shoplineV1Images)
            ? shoplineV1Images
            : String(shoplineV1Images || '').split(',');
          totalImages += imgs.filter(i => i && i.toString().trim()).length;
        }
      });

      this.stats.productCount = uniqueHandles.size;
      this.stats.variantCount = totalVariants;
      this.stats.imageCount = totalImages;
      this.stats.mappedFieldCount = this.fieldMappings.length;
    },

    /**
     * Generate Shopify CSV
     */
    generateShopifyCsv() {
      this.shopifyCsv = generateShopifyCsv(this.parsedData);
    },

    /**
     * Download the generated Shopify CSV
     */
    downloadShopifyCsv() {
      if (!this.shopifyCsv) {
        alert('No CSV data available. Please upload and process a file first.');
        return;
      }
      downloadFile(this.shopifyCsv, this.exportFileName, 'text/csv');
    },

    /**
     * Format number with commas
     */
    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    /**
     * Format file size for display
     */
    formatFileSize(bytes) {
      if (bytes < 1024) {return bytes + ' B';}
      if (bytes < 1024 * 1024) {return (bytes / 1024).toFixed(1) + ' KB';}
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    /**
     * Truncate value for preview
     */
    truncateValue(value, maxLength) {
      if (!value) {return '';}
      if (value.length <= maxLength) {return value;}
      return value.substring(0, maxLength) + '...';
    },

    /**
     * Reset all state
     */
    reset() {
      this.uploadedFile = null;
      this.fileName = null;
      this.fileSize = null;
      this.fileType = null;
      this.detectedPlatform = null;
      this.parsedData = [];
      this.headers = [];
      this.csvFormat = null;
      this.fieldMappings = [];
      this.showAllMappings = false;
      this.stats = {
        imageCount: 0,
        mappedFieldCount: 0,
        productCount: 0,
        variantCount: 0
      };
      this.processing = false;
      this.processingStage = '';
      this.processingProgress = 0;
      this.shopifyCsv = '';
      this.exportReady = false;
      this.exportFileName = '';

      // Reset steps
      this.steps.forEach((step, index) => {
        if (index === 0) {
          step.status = 'pending';
          step.subtitle = 'Waiting for file';
        } else {
          step.status = 'pending';
          step.subtitle = '';
        }
      });

      // Clear file input
      const fileInput = this.$refs.convertFileInput;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };
}
